import { useQueryClient } from "@tanstack/vue-query";

import type { FavouritesOnboardingStartedSource } from "~/analytics/segment";
import { preferencesQueryKeys } from "~/queries/catalog/preferences";
import { meQueryKeys, useUpdateMeMutation } from "~/queries/identity/me";

const sourceFromOnboardingMap: Record<
  OnboardingEntryType,
  FavouritesOnboardingStartedSource
> = {
  [OnboardingEntryType.AccountCreationPopup]: "ACCOUNT_CREATION_POPUP",
  [OnboardingEntryType.Follow]: "FOLLOWING",
  [OnboardingEntryType.Login]: "LOGIN",
  [OnboardingEntryType.Bid]: "NEW_SESSION",
  [OnboardingEntryType.ClubSso]: "CLUB_SSO",
  [OnboardingEntryType.OneTap]: "ONE_TAP",
};

const useOpenOnboardingDialog = () => {
  const { openOnboardingDialog, onboardingEntry, userCanPlaceTheirBid } =
    useOnboardingDialog();
  const platformStore = usePlatformStore();
  const queryClient = useQueryClient();
  const appStatusStore = useAppStatusStore();
  const { isPostPaymentVerificationSession } =
    useIsPostPaymentVerificationSession();
  const { isLoggedIn } = useAuth();

  const { shouldOpenVerificationsDialog } = useOpenVerificationsDialog();
  const { mutateAsync: updateUserMutateAsync } = useUpdateMeMutation();

  const hasReopenedInSession = useState("hasReopenedInSession", () => false);

  const featureToggles = useFeatureToggles();

  const checkOpenFavouritesFlow = async () => {
    const isLiverpool = getIsHostLiverpool();
    if (!featureToggles.value.customerPreferences || isLiverpool) return;

    const [preferences, userDetails] = await Promise.all([
      queryClient.fetchQuery(preferencesQueryKeys.list()),
      queryClient.fetchQuery(meQueryKeys.details()),
    ]);

    const hasUserCompletedOnboardingForm =
      userDetails.completedOnboardingForm ?? false;

    const userOnBoardingFormClosedCounter =
      userDetails?.onBoardingFormClosedCounter ?? 0;

    if (
      !preferences ||
      preferences.categoryIds?.length || // don't auto-toggle the modal if the user is already following some categories
      userOnBoardingFormClosedCounter === undefined
    )
      return;

    if (
      hasUserCompletedOnboardingForm === false &&
      userOnBoardingFormClosedCounter < 3
    ) {
      setTimeout(async () => {
        const source =
          onboardingEntry.value?.type &&
          sourceFromOnboardingMap[onboardingEntry.value.type];

        openOnboardingDialog({
          type: OnboardingEntryType.Follow,
          data: {
            source: source ?? "NEW_SESSION",
          },
        });

        updateUserMutateAsync({
          name: null,
          preferences: {
            completedOnboardingForm: false,
            onBoardingFormClosedCounter: userOnBoardingFormClosedCounter + 1,
          },
          nationalityCountryCode: null,
          dateOfBirth: null,
          gender: null,
          firstName: null,
          lastName: null,
        });

        hasReopenedInSession.value = true;
      }, 1000); // to give the user some time to see the page
    }
  };

  const checkOpenBiddingFlow = () => {
    if (
      isPostPaymentVerificationSession.value ||
      (!userCanPlaceTheirBid.value &&
        shouldOpenVerificationsDialog.value === "true")
    ) {
      openOnboardingDialog(onboardingEntry.value);

      shouldOpenVerificationsDialog.value = "false";

      hasReopenedInSession.value = true;

      return true;
    }

    return false;
  };

  const route = useRoute();

  const { shouldOpenVerification } = useNavigateAccountVerification();

  const checkAndOpenOnboardingDialog = () => {
    const preventedPaths = ["/verify-email", "/auth-redirect", "/my-account"];

    if (
      shouldOpenVerification.value === true ||
      preventedPaths.some((path) => route.path.includes(path)) ||
      !isLoggedIn.value ||
      platformStore.isFrameTheGame ||
      appStatusStore.getIsGiveawayPage ||
      hasReopenedInSession.value
    )
      return;

    // First check to see if the bidding flow should reopen
    const biddingOpen = checkOpenBiddingFlow();

    if (!biddingOpen) checkOpenFavouritesFlow();
  };

  return { checkAndOpenOnboardingDialog, hasReopenedInSession };
};

export default useOpenOnboardingDialog;
